import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import './App.css';

const Services = lazy(() => import('./pages/Services'));
const Projects = lazy(() => import('./pages/Projects'));
const About = lazy(() => import('./pages/About'));
const Blog = lazy(() => import('./pages/Blog'));
const Contact = lazy(() => import('./pages/Contact'));
const SolarCalculator = lazy(() => import('./pages/SolarCalculator'));
const GovernmentSchemes = lazy(() => import('./pages/GovernmentSchemes'));
const News = lazy(() => import('./pages/News'));
const CommercialRooftopSolar = lazy(() => import('./pages/CommercialRooftopSolar'));
const CommercialSolar = lazy(() => import('./pages/commercialSolar'));
const ResidentialSolar = lazy(() => import('./pages/ResidentialSolar'));
const IndustrialSolar = lazy(() => import('./pages/IndustrialSolar'));
const GroundMountedSolar = lazy(() => import('./pages/GroundMountedSolar'));
const OperationMaintenance = lazy(() => import('./pages/OperationMaintenance'));
const EvChargingStations = lazy(() => import('./pages/EvChargingStations'));
const BatteryEnergyStorageSystem = lazy(() => import('./pages/BatteryEnergyStorageSystem'));
const SolarStreetLights = lazy(() => import('./pages/SolarStreetLights'));
const SolarCarport = lazy(() => import('./pages/SolarCarport'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Footer = lazy(() => import('./components/Footer'));
const Map = lazy(() => import('./components/Map'));
const WhatsAppButton = lazy(() => import('./components/WhatsAppButton'));
const AudioManager = lazy(() => import('./components/AudioManager'));
const Chatbot = lazy(() => import('./components/Chatbot'));

function App() {
  const [deferredUiReady, setDeferredUiReady] = useState(false);

  useEffect(() => {
    let timeoutId;
    let idleId;

    const markReady = () => setDeferredUiReady(true);

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(markReady, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(markReady, 800);
    }

    return () => {
      if (idleId && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Header />
        <main className="main-content">
          <ErrorBoundary>
            <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/residential-solar" element={<ResidentialSolar />} />
                <Route path="/services/commercial-solar" element={<CommercialSolar />} />
                <Route path="/services/industrial-solar" element={<IndustrialSolar />} />
                <Route path="/services/commercial-rooftop-solar" element={<CommercialRooftopSolar />} />
                <Route path="/services/ground-mounted-solar" element={<GroundMountedSolar />} />
                <Route path="/services/operation-maintenance" element={<OperationMaintenance />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/about" element={<About />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/solar-calculator" element={<SolarCalculator />} />
                <Route path="/government-schemes" element={<GovernmentSchemes />} />
                <Route path="/news" element={<News />} />
                <Route path="/news/:id" element={<News />} />
                <Route path="/ev-charging-stations" element={<EvChargingStations />} />
                <Route path="/battery-energy-storage-system" element={<BatteryEnergyStorageSystem />} />
                <Route path="/solar-street-lights" element={<SolarStreetLights />} />
                <Route path="/solar-carport" element={<SolarCarport />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
        {deferredUiReady && (
          <Suspense fallback={null}>
            <Map />
            <Chatbot />
            <Footer />
            <WhatsAppButton floatingStyle={true} />
            <AudioManager />
          </Suspense>
        )}
      
      </div>
    </Router>
  );
}

export default App;
